import { FormLabel, FormControl, Button, FormHelperText } from '@mui/material';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import Chip from '@mui/material/Chip';
import { useTranslation } from '../../services';
import styles from './FileUploader.module.css';

export interface FileUploaderInterface {
	accept: string;
	error: boolean;
	value?: any[];
	onChange?: (data: any) => void;
	helper?: string | React.ReactElement;
	label: string;
	required?: boolean;
}

const FileUploader: FC<FileUploaderInterface> = React.forwardRef(
	({ accept, error, value, onChange, helper, label, required }, ref) => {
		const refFileInput = useRef<any>(null);
		const [files, setFiles] = useState<any[]>([]);
		const { t } = useTranslation();
		const [disabled, setDisabled] = useState<boolean>(Boolean);

		const handleChange = useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				const newFiles = event.target.files && Array.from(event.target.files);
				newFiles && setFiles([...files, ...newFiles]);
				refFileInput.current.value = '';
			},
			[files, setFiles]
		);

		const handleDelete = useCallback(
			(file: any) => {
				const updated = files.filter((saved) => file !== saved);
				setFiles(updated);
			},
			[files, setFiles]
		);

		// to clear when reset form
		useEffect(() => {
			if (files.length > 0 && (!value || value.length === 0)) {
				setFiles([]);
			}
		}, [value]);

		useEffect(() => {
			setDisabled(files.length >= 2);
			onChange && onChange(files);
		}, [files, onChange]);

		return (
			<FormControl
				sx={{ border: 1, borderRadius: 1, p: 3, boxSizing: 'border-box' }}
				fullWidth
				required={required}
				error={error}
				component="fieldset"
				variant="outlined"
			>
				<FormLabel component="legend" sx={{ p: '5px' }}>
					{label}:
				</FormLabel>
				<FormHelperText>{helper}</FormHelperText>
				<div className={styles.fileUploaderComponent}>
					{files.map((file, index) => (
						<Chip
							key={`${file.name}_${index}`}
							label={file.name}
							variant="outlined"
							sx={{ m: 1 }}
							onDelete={() => handleDelete(file)}
						/>
					))}
					<div className={styles.break}></div>
					<div className={disabled ? styles.fileUploaderButtonDisabled : styles.fileUploaderButton}>
						<Button
							variant="outlined"
							color="inherit"
							aria-label="assign request documents"
							component="label"
						>
							<input
								ref={refFileInput}
								hidden
								disabled={disabled}
								accept={accept}
								multiple
								type="file"
								onChange={handleChange}
							/>
							<FileUploadIcon /> {t('Upload')}
						</Button>
					</div>
				</div>
			</FormControl>
		);
	}
);

export default FileUploader;
